import { initRequest } from '../api/request.js';

const restApi = initRequest(process.env.BACKEND_API);

export async function getCollectionsById({ id, eventPrivateApiKey, eventPublicApiKey, withProductData = true }) {
  return restApi.post('/ecommerce/collections/get-by-id', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    id,
    withProductData,
  });
}

export async function getCollectionsByEventName(eventName) {
  return restApi.post(
    '/occo-owner/ecommerce/collections/get-by-event-name',
    {
      eventName,
    },
    { addAuth: true }
  );
}

export async function getCollectionsWithProductsByEventName({ eventPrivateApiKey, eventPublicApiKey, eventName }) {
  return restApi.post('/ecommerce/collections/get-by-event-name', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    eventName,
  });
}

export async function createShoppingCard({
  lineItems = [],
  shippingAddress = null,
  eventPrivateApiKey,
  eventPublicApiKey,
}) {
  return restApi.post('/ecommerce/cart/create', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    shipping_address: shippingAddress,
  });
}

export async function addLineItemsToCart({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/add-line-items', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    cart_id: cartId,
  });
}

export async function createDraftOrder({ attendeeUid, cartId, eventPrivateApiKey }) {
  return restApi.post('/ecommerce/order/create-draft-order', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    cart_id: cartId,
  });
}

export async function completeDraftOrder({
  attendeeUid,
  draftOrderId,
  employeeSecretCode,
  authRequired,
  isByCash,
  eventPrivateApiKey,
}) {
  return restApi.post('/ecommerce/order/complete-draft-order', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    draft_order_id: draftOrderId,
    employee_secret_code: employeeSecretCode,
    auth_required: authRequired,
    is_by_cash: isByCash,
  });
}

export async function completeShopifyDraftOrderSelfCheckout({
  attendeeUid,
  draftOrderId,
  gateway,
  tag,
  eventPrivateApiKey,
}) {
  return restApi.post('/ecommerce/order/complete-draft-order-self-checkout', {
    attendee_uid: attendeeUid,
    event_private_api_key: eventPrivateApiKey,
    draft_order_id: draftOrderId,
    gateway,
    tag,
  });
}

export async function updateCartLineItems({ cartId, lineItems, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/update-line-items', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    line_items: lineItems,
    cart_id: cartId,
  });
}

export async function updateCartShippingAddress({ cartId, shippingAddress, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/update-address', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
    shipping_address: shippingAddress,
  });
}

export async function applyDiscountToCart({ cartId, discountCode, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/apply-discount', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
    discount_code: discountCode,
  });
}

export async function getShoppingCard({ cartId, eventPrivateApiKey, eventPublicApiKey }) {
  return restApi.post('/ecommerce/cart/get-by-id', {
    event_private_api_key: eventPrivateApiKey,
    event_public_api_key: eventPublicApiKey,
    cart_id: cartId,
  });
}

export async function getProductsByEventName({ withCombos = true, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/products/get-by-event-name',
    {
      eventName,
      withCombos,
    },
    { addAuth: true }
  );
}

export async function getShopLocationCodeList({ eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/products/shop-location-codes/list',
    {
      eventName,
    },
    { addAuth: true }
  );
}

export async function createProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/create', extendedProductData, { addAuth: true });
}

export async function deleteProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/delete', extendedProductData, { addAuth: true });
}

export async function editProduct({ productData, eventName }) {
  const extendedProductData = {
    ...productData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/products/edit', extendedProductData, { addAuth: true });
}

export async function createCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/create', extendedCollectionData, { addAuth: true });
}

export async function editCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/edit', extendedCollectionData, { addAuth: true });
}

export async function deleteCollection({ collectionData, eventName }) {
  const extendedCollectionData = {
    ...collectionData,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/collections/delete', extendedCollectionData, { addAuth: true });
}

export async function importExternalOrders({ tag, startDate, endDate, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/order/import-external-orders',
    {
      eventName,
      tag,
      startDate,
      endDate,
    },
    { addAuth: true }
  );
}

export async function printOrderPhotos({ orderId, timeBasedKey, eventPrivateApiKey, eventName }) {
  return restApi.post(
    '/ecommerce/web-hook/print-order-photos',
    {
      event_private_api_key: eventPrivateApiKey,
      callUniqueKey: `${timeBasedKey}${orderId}`,
      eventName,
      orderId,
    },
    { addAuth: true }
  );
}

export async function getEventOrders({ startDate, endDate, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/order/get-orders-by-event-and-date',
    {
      eventName,
      startDate,
      endDate,
    },
    { addAuth: true }
  );
}

export async function addStoreSettings({ settingsData, eventName }) {
  const extendedSettingsData = {
    ...settingsData,
    event_code: settingsData.event_code ?? eventName,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/settings/create', extendedSettingsData, { addAuth: true });
}

export async function editStoreSettings({ settingsData, eventName }) {
  const extendedSettingsData = {
    ...settingsData,
    event_code: settingsData.event_code ?? eventName,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/settings/edit', extendedSettingsData, { addAuth: true });
}

export async function getStoreSettings({ eventName, eventCode }) {
  return restApi.post(
    '/occo-owner/ecommerce/settings/get-by-event-code',
    {
      event_code: eventCode ?? eventName,
      eventName,
    },
    { addAuth: true }
  );
}

export async function deleteStoreSettings({ settingsData, eventName }) {
  const extendedSettingsData = {
    ...settingsData,
    event_code: settingsData.event_code ?? eventName,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/settings/delete', extendedSettingsData, { addAuth: true });
}

function buildStoreLocationDto(locationData, eventName, { includeId = false } = {}) {
  const payload = {
    event_code: locationData.event_code ?? eventName,
    location_name: locationData.location_name,
    location_external_id: locationData.location_external_id,
    address1: locationData.address1,
    address2: locationData.address2,
    country: locationData.country,
    state: locationData.state,
    city: locationData.city,
    zip: locationData.zip,
    company: locationData.company,
    phone: locationData.phone,
    first_name: locationData.first_name,
    last_name: locationData.last_name,
    eventName,
  };

  if (includeId && locationData.id != null) {
    payload.id = locationData.id;
  }

  return payload;
}

export async function addStoreLocation({ locationData, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/location/create',
    buildStoreLocationDto(locationData, eventName),
    { addAuth: true }
  );
}

export async function editStoreLocation({ locationData, eventName }) {
  return restApi.post(
    '/occo-owner/ecommerce/location/edit',
    buildStoreLocationDto(locationData, eventName, { includeId: true }),
    { addAuth: true }
  );
}

export async function getStoreLocation({ eventName, eventCode }) {
  return restApi.post(
    '/occo-owner/ecommerce/location/get-by-event-code',
    {
      event_code: eventCode ?? eventName,
      eventName,
    },
    { addAuth: true }
  );
}

export async function getStoreSettingsForKiosk({ eventName, eventPublicApiKey }) {
  return restApi.post('/ecommerce/settings/get-by-event-code', {
    event_code: eventName,
    event_public_api_key: eventPublicApiKey,
  });
}

export async function getStoreLocationForKiosk({ eventName, eventPublicApiKey }) {
  return restApi.post('/ecommerce/location/get-by-event-code', {
    event_code: eventName,
    event_public_api_key: eventPublicApiKey,
  });
}

export async function deleteStoreLocation({ locationData, eventName }) {
  const extendedLocationData = {
    ...locationData,
    event_code: locationData.event_code ?? eventName,
    eventName,
  };
  return restApi.post('/occo-owner/ecommerce/location/delete', extendedLocationData, { addAuth: true });
}
